import { components } from '@api/rswss/generated';
import { TimeSyncService } from '@services/TimeSyncService';

import { SocketClient } from '@libs/socket';

import { AuthService } from '@features/auth/services/AuthService';

/*
    ____,-------------------------------,____
    \   |             Game              |   /
    /___|-------------------------------|___\
*/

const GAME_TOURNAMENT_URL = `${__RUNTIME.rswss}/tournaments/game/`;
const GAME_FASTGAME_URL = `${__RUNTIME.rswss}/fast-game/`;
const GAME_SPECTATOR_URL = `${__RUNTIME.rswss}/spectators/game/`;

export type IGameSocketEvents = components['schemas']['IWs_Game_Events'];

export class GamesSocket extends SocketClient<IGameSocketEvents> {
  constructor(
    public gameId: string,
    public deviceId: string,
    public mode: 'tournament' | 'fast-game' | 'spectator',
    seqFn: () => number,
    customToken?: string,
  ) {
    const urlFn = () => {
      const u = new URL(
        `${mode === 'fast-game' ? GAME_FASTGAME_URL : mode === 'tournament' ? GAME_TOURNAMENT_URL : GAME_SPECTATOR_URL}${gameId}`,
      );

      u.searchParams.append('device', deviceId);
      u.searchParams.append('seq', seqFn().toString());

      if (AuthService.instance.isAuthenticated) {
        u.searchParams.append('token', AuthService.instance.socketAccessTokenFactory());
      } else if (customToken) {
        u.searchParams.append('token', customToken);
      }

      return u.toString();
    };

    super(urlFn, { shouldReconnect: defaultShouldReconnect });
    initTimeSync(this);
  }
}

/*
    ____,-------------------------------,____
    \   |     Fast Game Matchmakin      |   /
    /___|-------------------------------|___\
*/

const FAST_GAME_URL = `${__RUNTIME.rswss}/fast-game/`;

export type IFastGamesSocketEvents = components['schemas']['IWs_FastGame_Events'];

export class FastGamesSocket extends SocketClient<IFastGamesSocketEvents> {
  constructor(
    public deviceId: string,
    public anonymousSecret: string | null,
  ) {
    const urlFn = () => {
      const u = new URL(FAST_GAME_URL);

      u.searchParams.append('device', deviceId);

      if (anonymousSecret) {
        u.searchParams.append('anonymousSecret', anonymousSecret);
      }

      if (AuthService.instance.isAuthenticated) {
        u.searchParams.append('token', AuthService.instance.socketAccessTokenFactory());
      }

      return u.toString();
    };

    super(urlFn, { shouldReconnect: defaultShouldReconnect });
    initTimeSync(this);
  }
}

/*
    ____,-------------------------------,____
    \   |      Games with Friends       |   /
    /___|-------------------------------|___\
*/

const FRIEND_GAME_URL = `${__RUNTIME.rswss}/friend-game/`;

export type IFriendGamesSocketEvents = components['schemas']['IWs_FriendGame_Events'];

export class FriendGamesSocket extends SocketClient<IFriendGamesSocketEvents> {
  constructor(
    public deviceId: string,
    public anonymousSecret: string | null,
  ) {
    const urlFn = () => {
      const u = new URL(FRIEND_GAME_URL);

      u.searchParams.append('device', deviceId);

      if (anonymousSecret) {
        u.searchParams.append('anonymousSecret', anonymousSecret);
      }

      if (AuthService.instance.isAuthenticated) {
        u.searchParams.append('token', AuthService.instance.socketAccessTokenFactory());
      }

      return u.toString();
    };

    super(urlFn, { shouldReconnect: defaultShouldReconnect });
    initTimeSync(this);
  }
}

/*
    ____,-------------------------------,____
    \   |             Helpers           |   /
    /___|-------------------------------|___\
*/

function initTimeSync(
  socket:
    | SocketClient<IFastGamesSocketEvents>
    | SocketClient<IGameSocketEvents>
    | SocketClient<IFriendGamesSocketEvents>,
) {
  socket.subscribe((e) => {
    if (e.$ === 'connected' || e.$ === 'reconnected') {
      socket.send('ts');
      return;
    }
    if (!e.$ && e.t === 'time') {
      TimeSyncService.instance.reportSocketTs(e.v);
    }
  });
}

function defaultShouldReconnect(code: number) {
  return code !== 1000 && code <= 4000;
}
